import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import {
  type CreateTrackedWalletInput,
  type TrackedWalletRule,
  type UpdateTrackedWalletInput,
  type UpdateWalletNotificationInput,
  WALLET_TRACKER_ENABLED,
  type WalletTrackerNotification,
  type WalletTrackerRulesResponse,
  createTrackedWallet,
  deleteTrackedWallet,
  fetchAllWalletNotifications,
  fetchWalletTracker,
  markAllWalletNotificationsRead,
  updateTrackedWallet,
  updateWalletNotification,
} from '../api/userland'

export type WalletTrackerStatus = 'disabled' | 'loading' | 'ready' | 'offline'

export type UseWalletTracker = {
  enabled: boolean
  status: WalletTrackerStatus
  rules: TrackedWalletRule[]
  limit: WalletTrackerRulesResponse['limit'] | null
  channel: WalletTrackerRulesResponse['channel'] | null
  notifications: WalletTrackerNotification[]
  lastSequence: number
  actionError: string | null
  clearActionError: () => void
  reloadRules: () => Promise<void>
  reseed: (afterSequence?: number) => Promise<void>
  acceptLive: (notification: WalletTrackerNotification) => {
    accepted: boolean
    gap: boolean
    previousSequence: number
  }
  createRule: (input: CreateTrackedWalletInput) => Promise<void>
  updateRule: (id: string, input: UpdateTrackedWalletInput) => Promise<void>
  deleteRule: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  updateNotification: (id: string, input: UpdateWalletNotificationInput) => Promise<void>
}

export function mergeWalletNotifications(
  current: WalletTrackerNotification[],
  incoming: WalletTrackerNotification[]
): WalletTrackerNotification[] {
  const bySequence = new Map<number, WalletTrackerNotification>()
  for (const notification of current) bySequence.set(notification.sequence, notification)
  for (const notification of incoming) bySequence.set(notification.sequence, notification)
  return [...bySequence.values()].sort((left, right) => right.sequence - left.sequence)
}

function trackerMissing(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 501)
}

function trackerError(error: unknown): string {
  if (trackerMissing(error)) return ''
  if (error instanceof ApiError) return error.message
  if (error instanceof Error && error.message) return error.message
  return 'Wallet tracker is unreachable'
}

/**
 * `accountKey` names the session's account (null when signed out). When it
 * changes — a sign-in, a switch, a sign-out without a page reload — the old
 * account's channel, rules and inbox are dropped and the tracker is read again
 * for the new one: the channel is that account's private WS capability (its
 * alerts and its rewards.update ride it).
 */
export function useWalletTracker(
  enabled = WALLET_TRACKER_ENABLED,
  accountKey: string | null = null
): UseWalletTracker {
  const [status, setStatus] = useState<WalletTrackerStatus>(enabled ? 'loading' : 'disabled')
  const [rules, setRules] = useState<TrackedWalletRule[]>([])
  const [limit, setLimit] = useState<WalletTrackerRulesResponse['limit'] | null>(null)
  const [channel, setChannel] = useState<WalletTrackerRulesResponse['channel'] | null>(null)
  const [notifications, setNotifications] = useState<WalletTrackerNotification[]>([])
  const [lastSequence, setLastSequence] = useState(0)
  const [actionError, setActionError] = useState<string | null>(null)
  const alive = useRef(true)
  // Bumped per account: a read started for the previous account never lands.
  const generation = useRef(0)
  const confirmedSequenceRef = useRef(0)
  const unresolvedSequencesRef = useRef(new Set<number>())

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const applyNotifications = useCallback((incoming: WalletTrackerNotification[]) => {
    if (incoming.length === 0) return
    for (const item of incoming) {
      if (Number.isSafeInteger(item.sequence) && item.sequence > confirmedSequenceRef.current) {
        unresolvedSequencesRef.current.add(item.sequence)
      }
    }
    let next = confirmedSequenceRef.current + 1
    while (unresolvedSequencesRef.current.delete(next)) {
      confirmedSequenceRef.current = next
      next += 1
    }
    setLastSequence(confirmedSequenceRef.current)
    setNotifications((current) => mergeWalletNotifications(current, incoming))
  }, [])

  // Every server answer lands only for the account it was asked for: a read
  // in flight across a sign-in or switch is dropped (its channel or inbox is
  // the previous account's).
  const answerFor = useCallback(() => {
    const gen = generation.current
    return () => alive.current && gen === generation.current
  }, [])

  const reloadRules = useCallback(async () => {
    if (!enabled) return
    const current = answerFor()
    const response = await fetchWalletTracker()
    if (!current()) return
    setRules(response.rules)
    setLimit(response.limit)
    setChannel(response.channel)
  }, [enabled, answerFor])

  const bootstrap = useCallback(async () => {
    if (!enabled) return
    const gen = generation.current
    const stale = () => !alive.current || gen !== generation.current
    setStatus('loading')
    try {
      const [tracker, inbox] = await Promise.all([
        fetchWalletTracker(),
        fetchAllWalletNotifications(0),
      ])
      if (stale()) return
      setRules(tracker.rules)
      setLimit(tracker.limit)
      setChannel(tracker.channel)
      confirmedSequenceRef.current = 0
      unresolvedSequencesRef.current.clear()
      for (const item of inbox) {
        if (Number.isSafeInteger(item.sequence) && item.sequence > 0) {
          unresolvedSequencesRef.current.add(item.sequence)
        }
      }
      while (unresolvedSequencesRef.current.delete(confirmedSequenceRef.current + 1)) {
        confirmedSequenceRef.current += 1
      }
      setLastSequence(confirmedSequenceRef.current)
      setNotifications(mergeWalletNotifications([], inbox))
      setStatus('ready')
    } catch (error) {
      if (stale()) return
      if (trackerMissing(error)) {
        setRules([])
        setNotifications([])
        setActionError(null)
        setStatus('ready')
        return
      }
      setActionError(trackerError(error))
      setStatus('offline')
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled')
      setRules([])
      setLimit(null)
      setChannel(null)
      setNotifications([])
      confirmedSequenceRef.current = 0
      unresolvedSequencesRef.current.clear()
      setLastSequence(0)
      return
    }
    void bootstrap()
  }, [bootstrap, enabled])

  // A new session account: drop what belonged to the previous one, read again.
  const firstAccount = useRef(true)
  // biome-ignore lint/correctness/useExhaustiveDependencies: accountKey is the trigger; bootstrap is stable per `enabled`
  useEffect(() => {
    if (firstAccount.current) {
      firstAccount.current = false
      return
    }
    generation.current += 1
    setChannel(null)
    setRules([])
    setLimit(null)
    setNotifications([])
    confirmedSequenceRef.current = 0
    unresolvedSequencesRef.current.clear()
    setLastSequence(0)
    if (enabled) void bootstrap()
  }, [accountKey])

  const reseed = useCallback(
    (afterSequence = confirmedSequenceRef.current): Promise<void> => {
      if (!enabled) return Promise.resolve()
      const repairCursor = Math.min(afterSequence, confirmedSequenceRef.current)
      const current = answerFor()
      return fetchAllWalletNotifications(repairCursor)
        .then((inbox) => {
          if (current()) applyNotifications(inbox)
        })
        .catch((error) => {
          if (current()) setActionError(trackerError(error))
          throw error
        })
    },
    [applyNotifications, enabled, answerFor]
  )

  const acceptLive = useCallback(
    (notification: WalletTrackerNotification) => {
      if (!enabled || !Number.isSafeInteger(notification.sequence) || notification.sequence <= 0) {
        return { accepted: false, gap: false, previousSequence: confirmedSequenceRef.current }
      }
      const previous = confirmedSequenceRef.current
      if (
        notification.sequence <= previous ||
        unresolvedSequencesRef.current.has(notification.sequence)
      ) {
        return { accepted: false, gap: false, previousSequence: previous }
      }
      applyNotifications([notification])
      const gap = unresolvedSequencesRef.current.size > 0
      return {
        accepted: true,
        gap,
        previousSequence: gap ? confirmedSequenceRef.current : previous,
      }
    },
    [applyNotifications, enabled]
  )

  const mutateRules = useCallback(
    async (mutation: () => Promise<unknown>) => {
      if (!enabled) return
      setActionError(null)
      try {
        await mutation()
        await reloadRules()
      } catch (error) {
        if (alive.current) setActionError(trackerError(error))
        throw error
      }
    },
    [enabled, reloadRules]
  )

  const updateInbox = useCallback(
    async (id: string, input: UpdateWalletNotificationInput) => {
      if (!enabled) return
      setActionError(null)
      const current = answerFor()
      try {
        const updated = await updateWalletNotification(id, input)
        if (current() && updated)
          setNotifications((current) => mergeWalletNotifications(current, [updated]))
      } catch (error) {
        if (alive.current) setActionError(trackerError(error))
        throw error
      }
    },
    [enabled, answerFor]
  )

  return {
    enabled,
    status,
    rules,
    limit,
    channel,
    notifications,
    lastSequence,
    actionError,
    clearActionError: useCallback(() => setActionError(null), []),
    reloadRules,
    reseed,
    acceptLive,
    createRule: useCallback(
      (input) => mutateRules(() => createTrackedWallet(input)),
      [mutateRules]
    ),
    updateRule: useCallback(
      (id, input) => mutateRules(() => updateTrackedWallet(id, input)),
      [mutateRules]
    ),
    deleteRule: useCallback((id) => mutateRules(() => deleteTrackedWallet(id)), [mutateRules]),
    markAllRead: useCallback(async () => {
      if (!enabled) return
      setActionError(null)
      const current = answerFor()
      try {
        await markAllWalletNotificationsRead()
        if (!current()) return
        const now = new Date().toISOString()
        setNotifications((current) =>
          current.map((item) =>
            item.archived_at || item.read_at ? item : { ...item, read_at: now }
          )
        )
      } catch (error) {
        if (alive.current) setActionError(trackerError(error))
        throw error
      }
    }, [enabled, answerFor]),
    updateNotification: updateInbox,
  }
}
